import { ApiRequestInfo } from 'types/apiRequestInfo';
import { sha256Hash } from 'utils/cryptoUtils';

/**
 * Recursively sorts object keys for deterministic JSON stringification.
 * Handles nested objects and arrays.
 */
function sortObjectKeys(obj: unknown): unknown {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }

  const sorted: Record<string, unknown> = {};
  Object.keys(obj as Record<string, unknown>)
    .sort()
    .forEach((key) => {
      sorted[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
    });
  return sorted;
}

/**
 * Generates a unique API key from request information for widget data caching.
 *
 * sha256 is used so the key can be sent over the wire to the backend
 * (`storeWidgetData`) as a fixed-length identifier. Async by necessity.
 *
 * IMPORTANT: normalises missing `data`/`params` as `{}` (not null) so that
 * keys match what the backend already has stored. Do not change this.
 */
export async function generateApiKey(
  requestInfo: ApiRequestInfo,
): Promise<string> {
  const { url, method, data, params } = requestInfo;

  const requestSignature = {
    url,
    method,
    data: data || {},
    params: params || {},
  };

  const sortedSignature = sortObjectKeys(requestSignature);
  return sha256Hash(JSON.stringify(sortedSignature));
}

/**
 * Generates a synchronous, deterministic API key for in-memory request
 * caching (ENG-123032 `cacheAPI`).
 *
 * Synchronous (no sha256) so that concurrent callers in the same tick cannot
 * race past the cache check before any of them inserts an entry.
 *
 * Uses `?? null` for normalisation (distinct key-space from `generateApiKey`
 * — these keys are never sent to the backend).
 */
export function generateApiKeySync(requestInfo: ApiRequestInfo): string {
  const { url, method, data, params } = requestInfo;
  // Normalise URL to always have a leading slash so 'api/foo' and '/api/foo'
  // produce the same cache key.
  const normalisedUrl = url?.startsWith('/') ? url : `/${url}`;
  const sorted = sortObjectKeys({
    url: normalisedUrl,
    method: method ?? 'GET',
    data: data ?? null,
    params: params ?? null,
  });
  return JSON.stringify(sorted);
}
